import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { useToast } from '@/composables/useToast';
import { AnalyticsEvents } from '@/services/analytics/events';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { useQueryClient } from '@tanstack/vue-query';
import { invalidatePerformanceAssessmentEntryQueries } from './performanceAssessmentEntryQueryInvalidation';
import { useRefreshSetupChecklist } from '../setupChecklist/useRefreshSetupChecklist';
import { invalidateGrowthCtaQueries } from '../growth/growthCtaQueryInvalidation';

export enum EPerformanceAssessmentCreationSource {
  Composer = 'composer',
  Dialog = 'dialog',
}

interface ICreatePerformanceAssessmentEntryOptions {
  performanceAssessmentCreationSource?: EPerformanceAssessmentCreationSource;
  suppressSuccessToast?: boolean;
}

interface ICreatePerformanceAssessmentEntryComposable {
  createPerformanceAssessmentEntry: (
    dto: Components.Schemas.CreatePerformanceAssessmentEntryDto,
    options?: ICreatePerformanceAssessmentEntryOptions,
  ) => Promise<Components.Schemas.PerformanceAssessmentEntryDto>;
  refreshPerformanceAssessmentEntryQueries: (schoolClassId?: number | null) => Promise<void>;
}

export const useCreatePerformanceAssessmentEntry = (): ICreatePerformanceAssessmentEntryComposable => {
  const { createSuccessToast, catchError } = useToast();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const trialUpsellStore = useTrialUpsellStore();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();

  const refreshPerformanceAssessmentEntryQueries = async (schoolClassId?: number | null): Promise<void> => {
    await invalidatePerformanceAssessmentEntryQueries(queryClient, schoolClassId);
  };

  const createPerformanceAssessmentEntry = async (
    dto: Components.Schemas.CreatePerformanceAssessmentEntryDto,
    options: ICreatePerformanceAssessmentEntryOptions = {},
  ): Promise<Components.Schemas.PerformanceAssessmentEntryDto> => {
    return catchError(async () => {
      const res = await Api.Routes.PerformanceAssessmentEntry.create(null, dto);
      trialUpsellStore.recordCompetenceObservationsCreated(dto.studentIds.length * dto.competenceIds.length);
      trialUpsellStore.requestEligibilityCheck();
      await Promise.all([
        refreshPerformanceAssessmentEntryQueries(dto.schoolClassId),
        refreshSetupChecklist(),
        invalidateGrowthCtaQueries(queryClient),
      ]);
      if (!options.suppressSuccessToast) createSuccessToast();
      trackEvent(AnalyticsEvents.performanceAssessment.entryCreated, {
        performanceAssessmentCreationSource:
          options.performanceAssessmentCreationSource ?? EPerformanceAssessmentCreationSource.Dialog,
      });
      return res.data;
    });
  };

  return {
    createPerformanceAssessmentEntry,
    refreshPerformanceAssessmentEntryQueries,
  };
};
